import React, { useEffect, useState } from "react";
import { Search, Sparkles, TrendingUp, TrendingDown, Minus, MessageCircle, Loader2, X, Info, Phone } from "lucide-react";
import { api, BRL } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const AMC_WHATSAPP = "5511956649968";

/**
 * Modal público para escolas consultarem a faixa de preços histórica
 * da AMC Escolas. Mostra apenas agregados (sem detalhes de outras escolas).
 *
 * Props:
 *  - open, onOpenChange
 *  - schoolName (optional) — pra personalizar a mensagem do WhatsApp
 */
export default function PriceConsultationDialog({ open, onOpenChange, schoolName = "" }) {
  const [q, setQ] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (!open) {
      setQ("");
      setStats(null);
      setSearched(false);
    }
  }, [open]);

  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) {
      setStats(null);
      setSearched(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/price-consultation", { params: { q: text } });
        setStats(r.data);
        setSearched(true);
      } catch {
        setStats({ count: 0, min: 0, max: 0, avg: 0, query: text });
        setSearched(true);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [q]);

  const waMessage = (() => {
    const prefix = schoolName ? `Olá! Sou da ${schoolName}.` : "Olá!";
    if (stats && stats.count > 0) {
      return encodeURIComponent(
        `${prefix} Fiz uma consulta no sistema sobre "${q}" e gostaria de confirmar um orçamento específico com vocês.`
      );
    }
    return encodeURIComponent(
      `${prefix} Procurei por "${q || "um serviço"}" na consulta de preços e não encontrei. Vocês conseguem me passar um orçamento?`
    );
  })();

  const waLink = `https://wa.me/${AMC_WHATSAPP}?text=${waMessage}`;
  const hasResult = stats && stats.count > 0;
  const noResult = searched && stats && stats.count === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="price-consultation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            Consulta de preços
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1 pl-11">
            Digite o serviço ou item que você precisa — se já tivermos feito algo parecido, mostramos a faixa de valor que a AMC costuma cobrar.
          </p>
        </DialogHeader>

        {/* Search input */}
        <div className="relative">
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ex.: troca de lâmpada, pintura, reparo em telhado..."
            className="pl-10 pr-10 h-12 text-base"
            data-testid="input-price-search"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              aria-label="Limpar"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* States */}
        {loading && (
          <div className="flex items-center justify-center py-8 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Consultando...
          </div>
        )}

        {!loading && !searched && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3" data-testid="price-tip">
            <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="text-xs text-blue-900 leading-relaxed">
              <p className="font-semibold mb-1">Como funciona:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Digite o nome do serviço (ex.: "lâmpada", "pintura")</li>
                <li>Mostramos o <b>menor</b>, o <b>maior</b> e o <b>valor médio</b> cobrado</li>
                <li>Se não acharmos, você pode falar direto com a AMC pelo WhatsApp</li>
              </ul>
            </div>
          </div>
        )}

        {!loading && hasResult && (
          <div className="space-y-3" data-testid="price-result-found">
            <div className="bg-gradient-to-br from-violet-50 via-indigo-50 to-sky-50 border border-violet-200 rounded-2xl p-4 sm:p-5">
              <p className="text-xs sm:text-sm text-slate-700">
                A <b className="text-[#7c3aed]">AMC Escolas</b> costuma cobrar para{" "}
                <b>"{q}"</b>:
              </p>
              <div className="grid grid-cols-3 gap-1.5 sm:gap-2 mt-3">
                <div className="bg-white rounded-xl px-1 py-2 sm:p-3 text-center border border-emerald-200 min-w-0">
                  <p className="text-[9px] sm:text-[10px] text-emerald-600 font-bold uppercase tracking-wide flex items-center justify-center gap-0.5">
                    <TrendingDown className="w-3 h-3" /> Mínimo
                  </p>
                  <p className="text-sm sm:text-lg md:text-xl font-black text-emerald-700 tabular-nums mt-1 whitespace-nowrap">
                    {BRL(stats.min)}
                  </p>
                </div>
                <div className="bg-white rounded-xl px-1 py-2 sm:p-3 text-center border-2 border-violet-300 shadow-md min-w-0">
                  <p className="text-[9px] sm:text-[10px] text-violet-600 font-bold uppercase tracking-wide flex items-center justify-center gap-0.5">
                    <Minus className="w-3 h-3" /> Média
                  </p>
                  <p className="text-sm sm:text-lg md:text-xl font-black text-violet-700 tabular-nums mt-1 whitespace-nowrap">
                    {BRL(stats.avg)}
                  </p>
                </div>
                <div className="bg-white rounded-xl px-1 py-2 sm:p-3 text-center border border-rose-200 min-w-0">
                  <p className="text-[9px] sm:text-[10px] text-rose-600 font-bold uppercase tracking-wide flex items-center justify-center gap-0.5">
                    <TrendingUp className="w-3 h-3" /> Máximo
                  </p>
                  <p className="text-sm sm:text-lg md:text-xl font-black text-rose-700 tabular-nums mt-1 whitespace-nowrap">
                    {BRL(stats.max)}
                  </p>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                * Baseado em <b>{stats.count}</b> orçamento{stats.count !== 1 ? "s" : ""} já realizado{stats.count !== 1 ? "s" : ""} com descrição parecida.
                O valor final depende de material, metragem, acabamento e condições específicas da escola.
              </p>
              {stats.note && (
                <div className="mt-2 pt-2 border-t border-violet-200 flex items-start gap-2">
                  <Info className="w-3.5 h-3.5 text-violet-700 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-violet-900 italic leading-relaxed">
                    <b>Observação da AMC:</b> {stats.note}
                  </p>
                </div>
              )}
            </div>

            {/* Important disclaimer — média histórica, não compromisso de valor */}
            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-xl p-3 sm:p-4" data-testid="price-disclaimer">
              <div className="flex gap-2 sm:gap-3">
                <Info className="w-4 h-4 sm:w-5 sm:h-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="text-[11px] sm:text-xs text-amber-900 leading-relaxed space-y-1.5">
                  <p className="font-black uppercase tracking-wide text-[10px] sm:text-[11px]">Importante — leia com atenção</p>
                  <p>
                    Os valores acima são uma <b>média histórica</b> do que a AMC Escolas cobrou em serviços
                    com descrição parecida ao longo do tempo. <b>Não é um orçamento fechado.</b>
                  </p>
                  <p>
                    Cada escola e cada serviço é único. Dois pedidos com o mesmo nome
                    (ex.: "pintura", "troca de lâmpada") podem ter preços bem diferentes dependendo de:
                  </p>
                  <ul className="list-disc list-inside pl-1 space-y-0.5">
                    <li>Quantidade / metragem / número de itens</li>
                    <li>Tipo de material escolhido (qualidade, marca)</li>
                    <li>Dificuldade de acesso ou altura (telhado, escada)</li>
                    <li>Problemas descobertos durante a <b>visita técnica</b></li>
                    <li>Reajustes de insumos e mão de obra ao longo do ano</li>
                  </ul>
                  <p>
                    Na <b>visita técnica</b>, podemos encontrar algo mais complexo do que o esperado — por isso o
                    valor base pode ser <b>alterado para mais ou para menos</b> antes de qualquer serviço começar.
                    O orçamento oficial sempre virá por escrito e precisa da sua aprovação.
                  </p>
                </div>
              </div>
            </div>

            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold shadow-lg transition-all hover:shadow-xl"
              data-testid="btn-wa-confirm"
            >
              <MessageCircle className="w-5 h-5" />
              Que tal confirmar este serviço com a AMC?
            </a>
          </div>
        )}

        {!loading && noResult && (
          <div className="space-y-3" data-testid="price-result-empty">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5 text-center">
              <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-3">
                <Phone className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="font-heading font-black text-slate-900 text-base">
                Ainda não temos este serviço no histórico
              </h3>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                Não se preocupe! A AMC Escolas atende <b>vários tipos de manutenção</b>.
                Chama a gente no WhatsApp que montamos um orçamento específico para a sua escola — rápido e sem compromisso.
              </p>
            </div>

            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full h-14 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold text-base shadow-lg transition-all hover:shadow-xl"
              data-testid="btn-wa-request"
            >
              <MessageCircle className="w-6 h-6" />
              Chamar AMC no WhatsApp
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
